import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {FilterInput} from 'src/components/filter-input';
import {DEBOUNCE_MS} from 'src/constants';
import {PB_ADMIN} from 'src/hooks/access-resource-constant';
import {useCurrentUser} from 'src/hooks/useCurrentUser';
import {usePageTitle} from 'src/hooks/usePageTitle';
import {useUserSelection} from 'src/hooks/useUserSelection';

import BoxLoading from '@pb/kit-component-box-loading';
import Button from '@pb/kit-component-button';
import Group from '@pb/kit-component-group';
import Typography from '@pb/kit-component-typography';


import {AddVendorDialog} from './components/add-vendor/add-vendor-dialog';
import {VendorManagementTable} from './components/vendor-management-table';
import {useVendorTableData} from './hooks/useVendorTableData';

import {mainHeader} from './vendor-management.module.styl';


export const VendorManagement = () => {
    usePageTitle('Client Portal: Entitlement Vendor Portal');
    const [showAddVendorDialog, setShowAddVendorDialog] = useState(false);
    const [filter, setFilter] = useState('');
    const [pagination, setPagination] = useState({page: 1, size: 25});
    const {hasAccessToResource} = useCurrentUser();
    const {vendors, loading, reload, rawVendors} = useVendorTableData(filter);
    const canCreateVendors = useMemo(() => hasAccessToResource(PB_ADMIN.CREATE_VENDOR), [hasAccessToResource]);

    const handleAddVendorClick = useCallback(() => {
        setShowAddVendorDialog(true);
    }, []);

    const handleCloseAddVendorDialog = useCallback(() => {
        setShowAddVendorDialog(false);
    }, []);

    const handleVendorCreated = useCallback(() => {
        reload();
    }, [reload]);

    const handleFilterChange = useCallback((value: string) => {
        setFilter(value);
        setPagination(prev => ({...prev, page: 1}));
    }, []);

    const handleClearFilter = useCallback(() => {
        setFilter('');
        setPagination(prev => ({...prev, page: 1}));
    }, []);

    const hasActiveFilter = filter.trim().length > 0;

    const totalCount = vendors.length;

    const pagedVendors = useMemo(() => {
        const start = (pagination.page - 1) * pagination.size;
        return vendors.slice(start, start + pagination.size);
    }, [pagination.page, pagination.size, vendors]);

    useEffect(() => {
        const maxPage = Math.max(1, Math.ceil(Math.max(totalCount, 1) / pagination.size));
        if (pagination.page > maxPage) {
            setPagination(prev => ({...prev, page: maxPage}));
        }
    }, [pagination.page, pagination.size, totalCount]);

    const {
        pageIds,
        selectedCount,
        allSelected,
        selectAll,
        selectPage,
        clearAll,
        toggle,
        isSelected,
    } = useUserSelection({
        rows: pagedVendors.map(v => v.id),
        totalCount,
    });
    const pageSelectedCount = pagedVendors.reduce((count, vendor) => count + (isSelected(vendor.id) ? 1 : 0), 0);

    const handleChangePage = useCallback((page: number) => {
        setPagination(prev => ({...prev, page}));
    }, []);

    const handleChangePageSize = useCallback((size: number) => {
        const nextSize = Math.max(size, 25);
        setPagination({page: 1, size: nextSize});
    }, []);

    return (
        <Group direction="v">
            <Group style={{padding: 16, paddingBottom: 0}} direction="v">
                <Typography
                    className={mainHeader}
                    variant="heading-level-1">
                    Vendor Management
                </Typography>
                <Group direction="h" justify="space-between" gap="m" style={{padding: 8}}>
                    <FilterInput
                        id="vendor-mgmt-filter"
                        value={filter}
                        onChange={handleFilterChange}
                        onClear={handleClearFilter}
                        timeout={DEBOUNCE_MS}
                        width={420}
                        placeholder="Search for vendors by vendor name or description"
                    />
                    {canCreateVendors ? (
                        <Button onClick={handleAddVendorClick} variant="Type.PRIMARY">
                            Add Vendor
                        </Button>
                    ) : null}
                </Group>
            </Group>
            <BoxLoading loading={loading}>
                <VendorManagementTable
                    vendors={pagedVendors}
                    hasActiveFilter={hasActiveFilter}
                    totalCount={totalCount}
                    pageIds={pageIds}
                    selectedCount={selectedCount}
                    allSelected={allSelected}
                    pageSelectedCount={pageSelectedCount}
                    onSelectAll={selectAll}
                    onSelectPage={selectPage}
                    onClearSelection={clearAll}
                    pagination={pagination}
                    onChangePage={handleChangePage}
                    onChangePageSize={handleChangePageSize}
                    isSelected={id => isSelected(id)}
                    onToggle={toggle}
                />
            </BoxLoading>

            <AddVendorDialog
                shown={showAddVendorDialog}
                onClose={handleCloseAddVendorDialog}
                existingVendors={rawVendors}
                onVendorCreated={handleVendorCreated}
            />
        </Group>
    );
};
